/**
 * Reactions on a post (fire, eyes, rocket, skull): counts from the batched `GET /posts/reactions`, an optimistic
 * toggle (`POST`/`DELETE /posts/:id/reactions`) that rolls back when the API refuses (429 included). Signed out, a
 * press asks to sign in. Glyphs are desaturated until the viewer has used them: colour stays quiet on the floor.
 */
import { useState } from 'react'
import type { FeedItem as FeedItemT, ReactionKind } from '@algoboo/schema/api'
import { cx, formatCount } from '@algoboo/ui'
import { isApiError } from '../../lib/api'
import { useRequireSession } from '../session'
import { REACTIONS, emptyReactions, usePostReactions, useToggleReaction } from './data'
import { ShareButton } from './ShareButton'

export function ReactionBar({ postId }: { postId: string }) {
  const q = usePostReactions(postId)
  const toggle = useToggleReaction(postId)
  const require = useRequireSession()
  const [popped, setPopped] = useState<ReactionKind | null>(null)
  const r = q.data ?? emptyReactions(postId)
  const limited = toggle.error && isApiError(toggle.error) && toggle.error.code === 'RATE_LIMITED'
  return (
    <div role="group" aria-label="Reactions" data-testid="reactions" className="flex items-center gap-0.5" title={limited ? 'Easy there: too many reactions, try again in a moment' : undefined}>
      {REACTIONS.map(({ kind, glyph, label }) => {
        const mine = r.mine.includes(kind)
        const n = r.counts[kind]
        return (
          <button
            key={kind}
            type="button"
            data-testid={`reaction-${kind}`}
            data-count={n}
            aria-pressed={mine}
            aria-label={`${label}${n ? `, ${n}` : ''}`}
            onClick={() =>
              require('Sign in to react to posts.', () => {
                setPopped(kind)
                toggle.mutate({ kind, on: !mine })
              })
            }
            onAnimationEnd={() => setPopped(null)}
            className={cx(
              'rh-press inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-[6px] border px-1.5 text-[12px] transition-colors',
              mine ? 'border-line-strong bg-surface-2 text-ink' : 'border-transparent text-ink-3 hover:border-line hover:text-ink',
              n === 0 && !mine && 'opacity-60 group-hover:opacity-100 focus-visible:opacity-100',
            )}
          >
            <span aria-hidden="true" className={cx('text-[13px] leading-none', !mine && 'grayscale', popped === kind && 'rh-react-pop')}>
              {glyph}
            </span>
            {n > 0 && <span className="font-mono text-[11px] tabular-nums">{formatCount(n, { compact: true })}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Footer actions of a feed item: reactions, and Share on trades and milestones. */
export function PostActions({ item }: { item: FeedItemT }) {
  const shareable = item.kind === 'trade' || (item.kind === 'note' && (item.payload as { type?: unknown }).type === 'milestone')
  return (
    <>
      <ReactionBar postId={item.id} />
      {shareable && <ShareButton handle={item.agent.handle} name={item.agent.name} text={item.body} compact />}
    </>
  )
}
